import { type KeyboardEvent, type ReactNode, useEffect, useState } from 'react';
import { formatInteger } from '@/utils/number';
import classes from './Table.module.css';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

interface TablePaginationRowProps {
  colSpan: number;
  page: number;
  pageCount: number;
  totalRecords: number;
  pageSize: number;
  canNext: boolean;
  canPrevious: boolean;
  next: VoidFunction;
  previous: VoidFunction;
  csv?: ReactNode;
  end?: ReactNode;
  onPageSizeChange?: (size: number) => void;
  onPageChange?: (pageIndex: number) => void;
  pageSizeOptions?: number[];
  hidePageSize?: boolean;
}

const TablePaginationRow = ({
  colSpan,
  page,
  pageCount,
  totalRecords,
  pageSize,
  canNext,
  canPrevious,
  next,
  previous,
  csv,
  end,
  onPageSizeChange,
  onPageChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  hidePageSize,
}: TablePaginationRowProps) => {
  const [pageInput, setPageInput] = useState(String(page));

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const applyPageChange = () => {
    const parsed = parseInt(pageInput, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      setPageInput(String(page));
      return;
    }
    const clamped = Math.min(parsed, pageCount);
    setPageInput(String(clamped));
    if (clamped !== page && onPageChange) {
      onPageChange(clamped - 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  if (!pageCount) return null;

  const safePage = Math.max(1, Math.min(page, pageCount));
  const startRecord = (safePage - 1) * pageSize + 1;
  const endRecord = Math.min(safePage * pageSize, totalRecords);

  return (
    <tfoot>
      <tr className={classes.tr}>
        <td colSpan={colSpan}>
          <div className={classes.paginationContent}>
            <div className={classes.paginationInfo}>
              <span className={classes.paginationLabel}>Page</span>
              <div className={classes.paginationPageGroup}>
                {onPageChange ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    className={classes.paginationPageNumber}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onBlur={applyPageChange}
                    onKeyDown={handleKeyDown}
                    aria-label="Go to page"
                    style={{ width: `${Math.max(4, String(pageCount).length + 2)}ch` }}
                  />
                ) : (
                  <span className={classes.paginationPageNumber}>{page}</span>
                )}
                <span className={classes.paginationRange}>
                  {formatInteger(startRecord)}-{formatInteger(endRecord)} of{' '}
                  {formatInteger(totalRecords)}
                </span>
              </div>
            </div>

            {!hidePageSize && onPageSizeChange && (
              <select
                className={classes.paginationPageSizeSelect}
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                aria-label="Rows per page"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}

            {pageCount > 1 && (
              <div className={classes.paginationNav}>
                <button
                  type="button"
                  className={classes.paginationNavButton}
                  onClick={previous}
                  disabled={!canPrevious}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className={classes.paginationNavButton}
                  onClick={next}
                  disabled={!canNext}
                >
                  Next
                </button>
              </div>
            )}

            {csv && (
              <div className={classes.paginationCsv} data-pagination-csv="">
                {csv}
              </div>
            )}

            {end && <div className={classes.paginationEnd}>{end}</div>}
          </div>
        </td>
      </tr>
    </tfoot>
  );
};

export default TablePaginationRow;
